import React, { useState, useEffect } from 'react';
import conceptosAfectosTareosService from '../services/conceptosAfectosTareosService';

const ConceptosAfectosTareosModal = ({ 
  isOpen, 
  onClose, 
  empresaId,
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conceptosIngresos, setConceptosIngresos] = useState([]);
  const [conceptosAfectos, setConceptosAfectos] = useState([]);
  const [conceptosSeleccionados, setConceptosSeleccionados] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && empresaId) {
      cargarDatos();
    }
  }, [isOpen, empresaId]);

  const cargarDatos = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Cargar conceptos de ingresos disponibles y conceptos ya configurados
      const [ingresosResponse, afectosResponse] = await Promise.all([
        conceptosAfectosTareosService.obtenerConceptosIngresosDisponibles(),
        conceptosAfectosTareosService.obtenerConceptosAfectos(empresaId)
      ]);

      if (ingresosResponse.success) {
        // Filtrar solo conceptos de tipo 'ingreso'
        const ingresosFiltered = ingresosResponse.data.filter(concepto => 
          concepto.tipo_concepto === 'ingreso'
        );
        setConceptosIngresos(ingresosFiltered);
      } else {
        setError('Error al cargar conceptos de ingresos');
      }

      if (afectosResponse.success) {
        setConceptosAfectos(afectosResponse.data);
        // Pre-seleccionar conceptos ya configurados
        const idsAfectos = new Set(afectosResponse.data.map(c => c.id_concepto_plame));
        setConceptosSeleccionados(idsAfectos);
      }

    } catch (error) {
      console.error('Error al cargar datos:', error);
      setError('Error al cargar la información');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleConcepto = (conceptoId) => {
    const nuevosSeleccionados = new Set(conceptosSeleccionados);
    
    if (nuevosSeleccionados.has(conceptoId)) {
      nuevosSeleccionados.delete(conceptoId);
    } else {
      nuevosSeleccionados.add(conceptoId);
    }
    
    setConceptosSeleccionados(nuevosSeleccionados);
  };

  const handleSelectAll = () => {
    const conceptosFiltrados = getConceptosFiltrados();
    const todosSeleccionados = conceptosFiltrados.every(c => conceptosSeleccionados.has(c.id));
    
    const nuevosSeleccionados = new Set(conceptosSeleccionados);
    
    if (todosSeleccionados) {
      // Deseleccionar todos los filtrados
      conceptosFiltrados.forEach(c => nuevosSeleccionados.delete(c.id));
    } else {
      // Seleccionar todos los filtrados
      conceptosFiltrados.forEach(c => nuevosSeleccionados.add(c.id));
    }
    
    setConceptosSeleccionados(nuevosSeleccionados);
  };

  const handleGuardar = async () => {
    setSaving(true);
    setError('');
    
    try {
      const conceptosIds = Array.from(conceptosSeleccionados);
      const response = await conceptosAfectosTareosService.configurarConceptosAfectos(
        empresaId, 
        conceptosIds
      );

      if (response.success) {
        onSuccess && onSuccess('Configuración de conceptos afectos a tareos guardada exitosamente');
        onClose();
      } else {
        let msg = response.error || 'Error al guardar la configuración';
        const errs = response.errors;
        if (errs && typeof errs === 'object') {
          const lines = Object.entries(errs).flatMap(([field, messages]) => {
            const arr = Array.isArray(messages) ? messages : [messages];
            return arr.map((m) => `${field}: ${m}`);
          });
          if (lines.length) msg = lines.join(' · ');
        }
        setError(msg);
      }
    } catch (error) {
      console.error('Error al guardar:', error);
      setError('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const getConceptosFiltrados = () => {
    if (!searchTerm) return conceptosIngresos;
    
    return conceptosIngresos.filter(concepto =>
      concepto.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      concepto.codigo.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const conceptosFiltrados = getConceptosFiltrados();
  const todosSeleccionados = conceptosFiltrados.length > 0 && 
    conceptosFiltrados.every(c => conceptosSeleccionados.has(c.id));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-50 to-blue-50 p-6 border-b border-teal-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <span className="text-2xl mr-3">💰</span>
                Conceptos Afectos a Tareos
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Selecciona qué conceptos PLAME de ingresos se verán afectados por los tareos
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Search and Actions */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Buscar por nombre o código..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>
              
              <button
                onClick={handleSelectAll}
                className="px-4 py-2 text-sm font-medium text-teal-700 bg-teal-100 rounded-lg hover:bg-teal-200 transition-colors"
              >
                {todosSeleccionados ? 'Deseleccionar Todo' : 'Seleccionar Todo'}
              </button>
            </div>

            {/* Estadísticas */}
            <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
              <span>
                {conceptosFiltrados.length} conceptos disponibles
              </span>
              <span>
                {conceptosSeleccionados.size} conceptos seleccionados
              </span>
            </div>
          </div>

          {/* Lista de Conceptos */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Cargando conceptos...</p>
                </div>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-red-700">{error}</p>
                </div>
              </div>
            ) : conceptosFiltrados.length === 0 ? (
              <div className="text-center py-12">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-500">No se encontraron conceptos</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {conceptosFiltrados.map((concepto) => (
                  <div
                    key={concepto.id}
                    className={`group relative border rounded-xl p-5 cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                      conceptosSeleccionados.has(concepto.id)
                        ? 'border-teal-500 bg-gradient-to-br from-teal-50 to-teal-100 shadow-lg ring-2 ring-teal-200'
                        : 'border-gray-200 bg-white hover:border-teal-300 hover:shadow-md hover:bg-gradient-to-br hover:from-gray-50 hover:to-white'
                    }`}
                    onClick={() => handleToggleConcepto(concepto.id)}
                  >
                    {/* Checkbox con mejor diseño */}
                    <div className="absolute top-4 right-4">
                      <div className={`relative w-5 h-5 rounded border-2 transition-all duration-200 ${
                        conceptosSeleccionados.has(concepto.id)
                          ? 'border-teal-500 bg-teal-500'
                          : 'border-gray-300 group-hover:border-teal-400'
                      }`}>
                        {conceptosSeleccionados.has(concepto.id) && (
                          <svg className="w-3 h-3 text-white absolute top-0.5 left-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>

                    {/* Icono principal */}
                    <div className="flex items-start space-x-4">
                      <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-200 ${
                        conceptosSeleccionados.has(concepto.id)
                          ? 'bg-teal-500 text-white'
                          : 'bg-gradient-to-br from-green-100 to-emerald-100 text-green-600 group-hover:from-teal-100 group-hover:to-teal-200 group-hover:text-teal-600'
                      }`}>
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                      </div>
                      
                      <div className="flex-1 min-w-0 pr-6">
                        {/* Nombre del concepto */}
                        <h4 className={`text-sm font-semibold mb-2 transition-colors duration-200 ${
                          conceptosSeleccionados.has(concepto.id)
                            ? 'text-teal-900'
                            : 'text-gray-900 group-hover:text-teal-800'
                        }`}>
                          {concepto.nombre}
                        </h4>
                        
                        {/* Código con diseño mejorado */}
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium transition-colors duration-200 ${
                            conceptosSeleccionados.has(concepto.id)
                              ? 'bg-teal-200 text-teal-800'
                              : 'bg-gray-100 text-gray-700 group-hover:bg-teal-100 group-hover:text-teal-700'
                          }`}>
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                            </svg>
                            {concepto.codigo}
                          </span>
                        </div>

                        {/* Clasificación */}
                        {concepto.clasificacion && (
                          <p className={`text-xs transition-colors duration-200 ${
                            conceptosSeleccionados.has(concepto.id)
                              ? 'text-teal-600'
                              : 'text-gray-500 group-hover:text-teal-600'
                          }`}>
                            {concepto.clasificacion}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Badge de tipo con mejor diseño */}
                    <div className="absolute bottom-4 right-4">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                        conceptosSeleccionados.has(concepto.id)
                          ? 'bg-emerald-500 text-white shadow-md'
                          : 'bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-700 group-hover:from-emerald-200 group-hover:to-green-200'
                      }`}>
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        Ingreso
                      </span>
                    </div>

                    {/* Indicador de selección */}
                    {conceptosSeleccionados.has(concepto.id) && (
                      <div className="absolute inset-0 rounded-xl pointer-events-none">
                        <div className="absolute top-2 left-2 w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end space-y-3 sm:space-y-0 sm:space-x-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleGuardar}
              disabled={saving || loading}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Guardando...</span>
                </>
              ) : (
                <span>Guardar Configuración</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConceptosAfectosTareosModal; 